import { useState } from 'react';

import {
  DialogCard,
  DialogHeaderV2,
  DialogStepperV2,
  makeDialog,
  Step,
  SwipeableViews,
} from '@cast/design-system';

import { SUPPORTS_COMMITMENTS } from 'constants/cluster';
import { useFilteredProvidersForOrganization } from 'hooks/useFilteredProvidersForOrganization';

import { InProgressSlide } from './InProgressSlide';
import { SelectProviderSlide } from './SelectProviderSlide';
import { SetupSlide } from './SetupSlide';
import { UploadCommitmentsDialogSlide } from './types';
import { UploadCommitmentsProvider } from './UploadCommitmentsProvider';
import { CommitmentProvider } from '../../types';

const UploadCommitmentsDialogContent = () => {
  const availableProviders = useFilteredProvidersForOrganization([
    ...SUPPORTS_COMMITMENTS,
  ]);

  const isMultiCloud = availableProviders.length > 1;
  const [activeSlide, setActiveSlide] = useState<UploadCommitmentsDialogSlide>(
    isMultiCloud ? 'selectProvider' : 'setup'
  );
  const [activeProvider, setActiveProvider] = useState<
    CommitmentProvider | undefined
  >(isMultiCloud ? undefined : availableProviders[0]);

  const setInProgress = () => {
    setActiveSlide('inProgress');
  };

  const showStepper =
    isMultiCloud && ['selectProvider', 'setup'].includes(activeSlide);

  return (
    <UploadCommitmentsProvider provider={activeProvider}>
      <DialogCard testId="upload-commitments-dialog">
        <DialogHeaderV2
          title="Upload commitments"
          endContent={
            showStepper ? (
              <DialogStepperV2
                activeStep={activeSlide === 'selectProvider' ? 0 : 1}
                inverse={false}
                sx={{
                  width: 180,
                  px: 40,
                  pt: 0,
                  position: 'absolute',
                  top: 18,
                  right: 20,
                }}
                testId="upload-commitments-stepper"
              >
                <Step label="Select provider" />
                <Step label="Upload" />
              </DialogStepperV2>
            ) : undefined
          }
        />
        <SwipeableViews
          animateHeight
          slides={{
            selectProvider: (
              <SelectProviderSlide
                onNext={() => setActiveSlide('setup')}
                setActiveProvider={setActiveProvider}
                activeProvider={activeProvider}
                availableProviders={availableProviders}
              />
            ),
            setup: (
              <SetupSlide
                isMultiCloud={isMultiCloud}
                onNext={setInProgress}
                onBack={() => setActiveSlide('selectProvider')}
                activeProvider={activeProvider}
              />
            ),
            inProgress: <InProgressSlide />,
          }}
          activeSlide={activeSlide}
        />
      </DialogCard>
    </UploadCommitmentsProvider>
  );
};

export const useUploadCommitmentsDialog = makeDialog(
  UploadCommitmentsDialogContent
);
